import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, Banknote, BadgeCheck, CheckCircle2, Wallet } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { offerings } from "@/mocks/residual";
import { formatNumber, formatUsd } from "@/lib/format";
type PaymentMethod = "crypto" | "fiat-onramp" | "fiat-processor";
type Step = "amount" | "pay" | "done";
export function PurchasePage({
  offeringId,
  onBack,
  onComplete
}: {
  offeringId: string;
  onBack: () => void;
  onComplete: () => void;
}) {
  const o = offerings.find(x => x.id === offeringId);
  const [step, setStep] = useState<Step>("amount");
  const [tokens, setTokens] = useState<number>(10);
  const [method, setMethod] = useState<PaymentMethod>("crypto");
  const total = useMemo(() => tokens * (o?.tokenPrice ?? 0), [tokens, o]);
  const platformFeePct = 1.5;
  const platformFee = method === "crypto" ? 0 : total * platformFeePct / 100;
  if (!o) {
    return <div className="space-y-4">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="size-4" /> Back
				</Button>
				<p className="text-sm text-muted-foreground">Offering not found.</p>
			</div>;
  }
  if (step === "done") {
    return <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 text-center">
				<div className="grid size-14 place-items-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
					<CheckCircle2 className="size-7" />
				</div>
				<div className="space-y-1">
					<h2 className="text-xl font-semibold">Purchase complete</h2>
					<p className="text-sm text-muted-foreground">
						{formatNumber(tokens)} {o.tokenSymbol} credited to your wallet
					</p>
				</div>
				<div className="rounded-lg border p-3 text-xs text-muted-foreground">
					Your ownership certificate is being generated and will be available
					in your portfolio shortly.
				</div>
				<Button onClick={onComplete} size="lg" className="mt-2 w-full max-w-xs">
					Go to portfolio
				</Button>
			</div>;
  }
  return <div className="space-y-5">
			<Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
				<ArrowLeft className="size-4" /> {step === "pay" ? "Back" : "Cancel"}
			</Button>

			<div>
				<h1 className="text-2xl font-semibold">Buy {o.tokenSymbol}</h1>
				<p className="text-sm text-muted-foreground">{o.title}</p>
			</div>

			{step === "amount" && <>
					<Card>
						<CardContent className="space-y-4 pt-6">
							<div className="space-y-2">
								<Label htmlFor="tokens">Tokens to purchase</Label>
								<Input id="tokens" type="number" min={1} max={o.tokenSupply - o.soldTokens} value={tokens} onChange={e => setTokens(Math.max(1, Number(e.target.value) || 0))} />
								<div className="flex flex-wrap gap-1.5">
									{[5, 10, 25, 100].map(q => <button key={q} type="button" onClick={() => setTokens(q)} className="rounded-full border px-3 py-1 text-xs font-medium hover:bg-muted">
											{q}
										</button>)}
								</div>
							</div>

							<Separator />

							<TotalRow label="Tokens" value={`${formatNumber(tokens)} × ${formatUsd(o.tokenPrice)}`} />
							<TotalRow label="Subtotal" value={formatUsd(total)} />
							<TotalRow label="Platform fee" value={method === "crypto" ? "Invoiced to partner" : formatUsd(platformFee)} hint={method === "crypto" ? "0%" : `${platformFeePct}%`} />
							<Separator />
							<TotalRow label="You pay" value={formatUsd(total + platformFee)} strong />
						</CardContent>
					</Card>

					<Button size="lg" className="w-full" onClick={() => setStep("pay")}>
						Continue to payment
					</Button>
				</>}

			{step === "pay" && <>
					<div className="space-y-2">
						<Label>Payment method</Label>
						<MethodPicker chain={o.chain} method={method} onChange={setMethod} />
					</div>

					<Card>
						<CardContent className="space-y-2 pt-6 text-sm">
							<TotalRow label="Total" value={formatUsd(total + platformFee)} strong />
							<p className="text-xs text-muted-foreground">
								{method === "crypto" ? `Send USDT on ${o.chain} to the partner's payment wallet. Tokens credited once 1 block confirmation lands.` : method === "fiat-onramp" ? "Fiat is converted to crypto via our on-ramp. Tokens credited to your custodial wallet within minutes of settlement." : "Pay the partner via their card processor. Platform commission is invoiced to the partner — you only see the headline price."}
							</p>
						</CardContent>
					</Card>

					<Button size="lg" className="w-full" onClick={() => setStep("done")}>
						{method === "crypto" ? "Open wallet to pay" : "Pay with card"}
					</Button>
					<p className="text-center text-xs text-muted-foreground">
						<BadgeCheck className="mr-1 inline size-3.5" />
						KYC verified · jurisdiction allowed
					</p>
				</>}
		</div>;
}
function MethodPicker(t0) {
  const $ = _c(10);
  const {
    chain,
    method,
    onChange
  } = t0;
  const t1 = `USDT on ${chain}`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      id: "crypto",
      title: t1,
      desc: "Self-custody \u2014 no platform fee at checkout",
      icon: Wallet
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      id: "fiat-onramp",
      title: "Card / bank (on-ramp)",
      desc: "Tokens land in a platform-managed custodial wallet",
      icon: Banknote
    };
    t4 = {
      id: "fiat-processor",
      title: "Card via partner processor",
      desc: "Pay the partner directly \u2014 tokens credited on webhook",
      icon: Banknote
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2) {
    t5 = [t2, t3, t4];
    $[4] = t2;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const opts = t5;
  let t6;
  if ($[6] !== method || $[7] !== onChange || $[8] !== opts) {
    t6 = <div className="space-y-2">{opts.map(o => {
        const Icon = o.icon;
        const active = method === o.id;
        return <button key={o.id} type="button" onClick={() => onChange(o.id)} className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${active ? "border-foreground bg-muted/30" : "hover:bg-muted/40"}`}><div className="grid size-9 place-items-center rounded-lg bg-muted"><Icon className="size-4" /></div><div className="flex-1"><div className="text-sm font-medium">{o.title}</div><div className="text-xs text-muted-foreground">{o.desc}</div></div>{active && <Badge variant="secondary" className="rounded-full font-normal">Selected</Badge>}</button>;
      })}</div>;
    $[6] = method;
    $[7] = onChange;
    $[8] = opts;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function TotalRow(t0) {
  const $ = _c(12);
  const {
    label,
    value,
    hint,
    strong
  } = t0;
  const t1 = strong ? "font-semibold" : "text-muted-foreground";
  let t2;
  if ($[0] !== label || $[1] !== t1) {
    t2 = <span className={t1}>{label}</span>;
    $[0] = label;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = strong ? "text-base font-semibold" : "font-medium tabular-nums";
  let t4;
  if ($[3] !== hint) {
    t4 = hint && <span className="ml-1 text-xs text-muted-foreground">({hint})</span>;
    $[3] = hint;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4 || $[7] !== value) {
    t5 = <span className={t3}>{value}{t4}</span>;
    $[5] = t3;
    $[6] = t4;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t5) {
    t6 = <div className="flex items-baseline justify-between text-sm">{t2}{t5}</div>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}